// Path: ./src/app/pages/CreateFlyerItemListPage/useFlyerItemListTableColumns.tsx
import { GridRenderCellParams } from "@mui/x-data-grid";
import { Link, Chip, IconButton, Tooltip } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import { messages } from "../../../locales/messages";
import { AllowedTableColumn } from "../../../utils/types/AllowTableTypes";
import { useTranslation } from "react-i18next";
import {
  GfFlyerItemList,
  SettingKey,
  AiGenerationStatus,
  FlyerPublicationStatus,
} from "@swagger/typescript-fetch-goflyer";
import { makeStoreDisplayName } from "../../../utils/dtoUtil/makeStoreDisplayName";
import { RenderEditButton } from "../../components/RenderEditButtonTable";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import RateReviewIcon from "@mui/icons-material/RateReview";
import { CopyButton } from "./styled";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useTableSettingsConfig,
  applyColumnsConfig,
} from "../../../hooks/useTableSettingsConfig";
import { routes } from "../../../utils/constant/navigationRoutes";
import { buildAiGenerationEffortColumn } from "../../components/AiGenerationEffortColumn";
import { hasAiGenerationRunDetails } from "./AiErrorLogModal";

const buildFlyerViewUrl = (row: GfFlyerItemList) => {
  const baseUrl = process.env.REACT_APP_GOFLYER_WEB_POINT;
  if (!baseUrl || !row?.flyer?.id) {
    return undefined;
  }
  const normalizedBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  return `${normalizedBase}/flyerview/${row.flyer.id}`;
};

/**
 * useFlyerItemListTableColumns Hook
 *
 * This hook defines the columns configuration for the Flyer Item List table.
 * It includes columns for edit button, merchant name, valid start date, valid end date,
 * updated date, created date, AI generation status, and AI error log
 *
 * @hook
 * @name useFlyerItemListTableColumns
 * @param {Function} handleOnEditClick - Callback function for handling edit button clicks.
 * @param {Function} handleErrorLogClick - Callback function for handling error log button clicks.
 * @returns {AllowedTableColumn[]} - An array of table column configurations.
 */
export const useFlyerItemListTableColumns = (
  handleOnEditClick: (value: GfFlyerItemList, copy?: boolean) => void,
  handleErrorLogClick?: (itemList: GfFlyerItemList) => void,
  showRetryCoordinates = false,
  handleRetryCoordinates?: (row: GfFlyerItemList) => void,
  retryingFlyerIds: string[] = [],
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { columnsConfig } = useTableSettingsConfig(
    SettingKey.TableFlyerItemLists,
  );

  const canOpenFlyerReview = (status?: FlyerPublicationStatus | string) =>
    status === FlyerPublicationStatus.APPROVED ||
    status === FlyerPublicationStatus.PENDINGREVIEW ||
    status === "APPROVED" ||
    status === "PENDING_REVIEW";
  const values: AllowedTableColumn[] = [
    "validStartDate",
    "validEndDate",
    "updatedAt",
    "createdAt",
  ];

  const baseColumns = [
    {
      field: "edit",
      width: 90,
      headerName: `${t(messages.Edit())}`,
      filterable: false,
      renderCell: (props: GridRenderCellParams) => {
        return (
          <PermissionComponentWrapper
            requiredPermission={["edit"]}
            requiredPermissionGroup={["flyer"]}
          >
            <RenderEditButton
              onClick={handleOnEditClick}
              {...props}
            ></RenderEditButton>
          </PermissionComponentWrapper>
        );
      },
      sortable: false,
    },
    {
      field: "copy",
      width: 90,
      headerName: `${t(messages.Copy())}`,
      filterable: false,
      renderCell: (props: GridRenderCellParams) => {
        return (
          <CopyButton
            startIcon={<FileCopyIcon />}
            onClick={(event) => {
              event.stopPropagation();
              handleOnEditClick(props.row, true);
            }}
            variant="outlined"
            size="small"
          >
            {t(messages.Copy())}
          </CopyButton>
        );
      },
      sortable: false,
    },
    {
      field: "merchant",
      width: 200,
      headerName: `${t(messages["Merchant"]())}`,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GfFlyerItemList>) => {
        const store = params.row.stores[0];
        const storeName = makeStoreDisplayName(store);
        return <div title={storeName?.trim()}>{storeName}</div>;
      },
    },
    {
      field: "flyers",
      width: 160,
      headerName: `${t(messages.Flyers())}`,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<GfFlyerItemList>) => {
        const flyerUrl = buildFlyerViewUrl(params.row);
        if (!flyerUrl) {
          return <div>-</div>;
        }

        return (
          <Link
            href={flyerUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
          >
            {t(messages.ViewFlyer())}
          </Link>
        );
      },
    },
    {
      field: "linkedFlyer",
      width: 160,
      headerName: `${t(messages.Open_In_Flyer_Editor())}`,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<GfFlyerItemList>) => {
        const flyerId = params.row.flyer?.id;
        if (!flyerId) {
          return <div>-</div>;
        }

        return (
          <Link
            href={routes.uploadFlyer.editById(flyerId)}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
          >
            {t(messages.Open_In_Flyer_Editor())}
          </Link>
        );
      },
    },
    ...values.map((value) => {
      return {
        width: 200,
        field: value,
        headerName: `${t(messages[value]())}`,
        type: "dateTime",
        valueGetter: (val: GridRenderCellParams) => {
          return val && new Date(val?.row[value]);
        },
      };
    }),

    {
      field: "CreatedBy",
      width: 200,
      headerName: `${t(messages["CreatedBy"]())}`,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<GfFlyerItemList>) => {
        const { firstName, lastName, userName, email } =
          params?.row?.adminUser || {};
        const createdBy = firstName
          ? `${firstName} ${lastName || ""}`
          : userName || email || "";

        return <div title={createdBy.trim()}>{createdBy}</div>;
      },
    },
    {
      field: "aiGenerationStatus",
      width: 180,
      headerName: "AI Generation Status",
      sortable: true,
      type: "singleSelect",
      valueOptions: [
        { value: AiGenerationStatus.WAITING, label: "Waiting" },
        {
          value: AiGenerationStatus.PROCESSING,
          label: t(messages.Processing()),
        },
        { value: AiGenerationStatus.COMPLETED, label: "Completed" },
        {
          value: AiGenerationStatus.PARTIALLYCOMPLETED,
          label: "Partially Completed",
        },
        { value: AiGenerationStatus.FAILED, label: "Failed" },
      ],
      renderCell: (params: GridRenderCellParams<GfFlyerItemList>) => {
        const status = params.row.aiGenerationStatus;
        if (!status) return <div>-</div>;

        const statusColors: Record<
          string,
          "default" | "info" | "success" | "warning" | "error"
        > = {
          [AiGenerationStatus.WAITING]: "default",
          [AiGenerationStatus.PROCESSING]: "info",
          [AiGenerationStatus.COMPLETED]: "success",
          [AiGenerationStatus.PARTIALLYCOMPLETED]: "warning",
          [AiGenerationStatus.FAILED]: "error",
        };

        const statusLabels: Record<string, string> = {
          [AiGenerationStatus.WAITING]: "Waiting",
          [AiGenerationStatus.PROCESSING]: t(messages.Processing()),
          [AiGenerationStatus.COMPLETED]: "Completed",
          [AiGenerationStatus.PARTIALLYCOMPLETED]: "Partially Completed",
          [AiGenerationStatus.FAILED]: "Failed",
        };

        return (
          <Chip
            label={statusLabels[status] || status}
            color={statusColors[status] || "default"}
            size="small"
          />
        );
      },
    },
    {
      field: "aiGenerationModel",
      width: 160,
      headerName: "Model",
      sortable: true,
      filterable: false,
      renderCell: (params: GridRenderCellParams<GfFlyerItemList>) => (
        <div title={params.row.aiGenerationModel ?? undefined}>
          {params.row.aiGenerationModel ?? "—"}
        </div>
      ),
    },
    buildAiGenerationEffortColumn<GfFlyerItemList>(
      (row) => row.aiGenerationEffort,
    ),
    {
      field: "aiGenerationErrorLog",
      width: 160,
      headerName: "AI Error Log",
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<GfFlyerItemList>) => {
        const itemList = params.row;
        const status = itemList.aiGenerationStatus;
        if (
          status !== AiGenerationStatus.FAILED &&
          status !== AiGenerationStatus.PARTIALLYCOMPLETED
        ) {
          return <div>-</div>;
        }

        const errorLog = itemList.aiGenerationErrorLog;
        if (!errorLog && !hasAiGenerationRunDetails(itemList)) {
          return <div>-</div>;
        }

        const hasErrors = errorLog
          ? errorLog.failedImages > 0 || Boolean(errorLog.generalError)
          : Boolean(itemList.aiGenerationErrorType);

        return (
          <Tooltip
            title={
              errorLog
                ? hasErrors
                  ? `${errorLog.failedImages} failed of ${errorLog.totalImages} total`
                  : "No Errors"
                : "AI Run Details"
            }
          >
            <IconButton
              size="small"
              color={hasErrors ? "error" : "success"}
              onClick={(event) => {
                event.stopPropagation();
                if (handleErrorLogClick) {
                  handleErrorLogClick(itemList);
                }
              }}
            >
              <ErrorOutlineIcon />
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: "review",
      width: 90,
      headerName: `${t(messages.Adjust())}`,
      filterable: false,
      sortable: false,
      renderCell: (params: GridRenderCellParams<GfFlyerItemList>) => {
        const flyerId = params.row.flyer?.id;
        const status = params.row.flyer?.publicationStatus;
        if (!flyerId || !canOpenFlyerReview(status)) {
          return null;
        }
        return (
          <Tooltip title={t(messages.Adjust())}>
            <IconButton
              size="small"
              color="primary"
              onClick={(event) => {
                event.stopPropagation();
                navigate(routes.adminFlyerReview.byId(flyerId));
              }}
              data-testid={`flyer-review-button-${flyerId}`}
            >
              <RateReviewIcon />
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: "reviewStatus",
      width: 200,
      headerName: `Review Status`,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<GfFlyerItemList>) => {
        const smartFlyer = params?.row?.reviewStatus;

        return smartFlyer ? (
          <Chip
            label={params?.row?.reviewStatus}
            size="small"
            color="info"
            sx={{
              fontSize: "12px",
              height: "22px",
              backgroundColor: "#f58220",
              color: "#fff",
              fontWeight: "500",
            }}
          />
        ) : (
          <></>
        );
      },
    },
    ...(showRetryCoordinates
      ? [
          {
            field: "retryCoordinates",
            width: 110,
            headerName: `${t(messages.Retry_Coordinates())}`,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams<GfFlyerItemList>) => {
              const status = params.row.aiGenerationStatus;
              if (
                status !== AiGenerationStatus.FAILED &&
                status !== AiGenerationStatus.PARTIALLYCOMPLETED
              ) {
                return null;
              }

              const flyerId = params.row.flyer?.id;
              const isRetrying = flyerId
                ? retryingFlyerIds.includes(flyerId)
                : false;

              return (
                <Tooltip
                  title={
                    isRetrying
                      ? t(messages.Processing())
                      : t(messages.Retry_Coordinates())
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      disabled={!handleRetryCoordinates || !flyerId || isRetrying}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (handleRetryCoordinates) {
                          handleRetryCoordinates(params.row);
                        }
                      }}
                    >
                      <ReplayIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              );
            },
          },
        ]
      : []),
  ];

  const columns = useMemo(
    () => applyColumnsConfig(baseColumns, columnsConfig),
    [baseColumns, columnsConfig],
  );

  return columns;
};
